import { Box, Button, Checkbox, Input, Popup, Table, TopBar } from '@sam/components';
import { TableHeader, TableRow } from '@sam/components/src/Table/Table.types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { generateNotification } from 'shared';
import {
  createNewCurrency,
  createNewCurrencyConversion,
  getAllConversionsForCurrency,
  updateCurrency,
  updateCurrencyConversion,
} from 'shared/src/currency/Currency.axios';
import { Currency, CurrencyConversion } from 'shared/src/currency/Currency.types';
import { NotificationType } from 'shared/src/notification/notification.types';
import { SaveButtons } from '../../components/saveButtons/SaveButtons';
import { useUser } from '../../components/UserContext';
import {
  convertConversionsIntoTableEntries,
  generateEmptyCurrency,
  generateEmptyCurrencyConversion,
} from '../../utils/currency/Currency.utils';
import { IconComponent, IconType } from '../../components/icons/IconComponent';
import { preventSubmitOnEnter } from '../../utils/general.utils';

export const CurrencyEdit: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { axios } = useUser();
  const location = useLocation<{ currency?: Currency }>();

  const button = useRef<HTMLButtonElement>(null);
  const form = useRef<HTMLFormElement>(null);

  const [currencyToEdit, setCurrencyToEdit] = useState<Currency>(
    location.state?.currency ? location.state.currency : generateEmptyCurrency(),
  );
  const [conversions, setConversions] = useState<CurrencyConversion[]>([]);
  const [conversionToAdd, setConversionToAdd] = useState<CurrencyConversion>();
  /**
   * TableRows to be displayed with all conversions
   */
  useEffect(() => {
    if (!currencyToEdit.id) return;
    else getAllConversionsForCurrency(axios, currencyToEdit.id).then(setConversions);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyToEdit.id]);

  /**
   * Holds rows with currencyConversions for the active currency
   */
  const conversionRows: TableRow[] = useMemo(
    (): TableRow[] => convertConversionsIntoTableEntries(conversions, setConversionToAdd),
    [conversions],
  );

  /**
   * Helper method to create or update a currencyConversion
   */
  const handleSubmitConversion = (): void => {
    if (!conversionToAdd) return;
    if (!conversionToAdd.id)
      createNewCurrencyConversion(axios, conversionToAdd).then((savedConversion) => {
        savedConversion && setConversions([...conversions, savedConversion]);
        setConversionToAdd(undefined);
      });
    else
      updateCurrencyConversion(axios, conversionToAdd).then((savedConversion) => {
        savedConversion &&
          setConversions(
            conversions.map((conversion) =>
              conversion.id === savedConversion.id ? savedConversion : conversion,
            ),
          );
        setConversionToAdd(undefined);
      });
  };

  /**
   * Submit handler to create or update a currency on submit
   * @param redirect decides if the method should navigate back after success
   */
  const handleSubmit = (redirect: boolean): void => {
    button.current?.click();
    if (!form.current?.checkValidity()) return;

    if (currencyToEdit.id) {
      updateCurrency(axios, currencyToEdit).then((updatedCurrency) => {
        if (updatedCurrency && redirect) navigate('/currency');
        else if (updatedCurrency) {
          setCurrencyToEdit(updatedCurrency);
          generateNotification({
            type: NotificationType.SUCCESS,
            value: t('general.notification.success.saveSuccessfull'),
          });
        }
      });
    } else {
      createNewCurrency(axios, currencyToEdit).then((createdCurrency) => {
        if (createdCurrency && redirect) navigate('/currency');
        else if (createdCurrency) {
          setCurrencyToEdit(createdCurrency);
          generateNotification({
            type: NotificationType.SUCCESS,
            value: t('general.notification.success.saveSuccessfull'),
          });
        }
      });
    }
  };
  return (
    <form ref={form} onSubmit={(evt) => evt.preventDefault()} onKeyDown={preventSubmitOnEnter}>
      <TopBar
        onBackClick={() => navigate(-1)}
        title={t(`pages.currency.edit.topBarHeadline${currencyToEdit.id ? 'Edit' : 'Create'}`)}
      >
        <SaveButtons handleSubmit={handleSubmit} buttonRef={button} />
      </TopBar>
      {conversionToAdd && (
        <Popup
          isOpen={!!conversionToAdd}
          onClose={() => setConversionToAdd(undefined)}
          buttons={[
            <Button
              value={t('general.buttons.cancel')}
              onClick={() => setConversionToAdd(undefined)}
            />,
            <Button value={t('general.buttons.save')} onClick={() => handleSubmitConversion()} />,
          ]}
        >
          <Input
            label={t('pages.currency.edit.factor')}
            type="number"
            value={conversionToAdd?.factor}
            onChangeNumber={(factor) => setConversionToAdd({ ...conversionToAdd, factor })}
          />
          <Input
            value={conversionToAdd?.period?.startDate}
            label={t('pages.currency.edit.startDate')}
            type="date"
            onChangeDate={(startDate) =>
              startDate &&
              setConversionToAdd({
                ...conversionToAdd,
                period: { ...conversionToAdd.period, startDate },
              })
            }
          />
          <Input
            value={conversionToAdd?.period?.endDate}
            label={t('pages.currency.edit.endDate')}
            type="date"
            onChangeDate={(endDate) =>
              endDate &&
              setConversionToAdd({
                ...conversionToAdd,
                period: { ...conversionToAdd.period, endDate },
              })
            }
          />
        </Popup>
      )}
      <Box>
        <div className="three-columns">
          <Input
            type="text"
            onChange={(title) => setCurrencyToEdit({ ...currencyToEdit, title })}
            label={t('pages.currency.edit.title')}
            value={currencyToEdit.title}
          />
          <Input
            type="text"
            onChange={(symbol) => setCurrencyToEdit({ ...currencyToEdit, symbol })}
            label={t('pages.currency.edit.symbol')}
            value={currencyToEdit.symbol}
          />
          <Input
            type="text"
            onChange={(intlCode) => setCurrencyToEdit({ ...currencyToEdit, intlCode })}
            label={t('pages.currency.edit.intlCode')}
            value={currencyToEdit.intlCode}
          />
          <Checkbox
            label={t('pages.currency.edit.deactivated')}
            isChecked={currencyToEdit.disabled}
            onCheck={(disabled) => setCurrencyToEdit({ ...currencyToEdit, disabled })}
          />
        </div>
      </Box>
      <Box>
        {currencyToEdit.intlCode === 'CZK' && (
          <IconComponent
            type={IconType.ADD}
            className="currency-edit__add"
            onClick={() =>
              setConversionToAdd(
                generateEmptyCurrencyConversion({
                  currencyId: currencyToEdit.id,
                }),
              )
            }
          />
        )}
        <Table
          rows={conversionRows}
          header={
            t('pages.currency.edit.tableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
    </form>
  );
};
